/**
 * deviceFingerprint — V1.5.8.2 anti-abuse.
 *
 * Client-side fingerprint hash sent alongside referral attach so the
 * backend cluster-check can catch IP-rotating self-referrers (VPN,
 * mobile-tether hopping).
 *
 * Signal sources (all cheap & non-identifying on their own):
 *   - navigator.userAgent
 *   - navigator.language
 *   - navigator.hardwareConcurrency
 *   - screen.width × screen.height × screen.colorDepth
 *   - Intl.DateTimeFormat().resolvedOptions().timeZone
 *   - a small canvas render string (font+glyph anti-aliasing signature)
 *
 * The hash is SHA-256 → hex, so backend validation is trivial. We cache
 * the result in `sessionStorage` for the tab's lifetime so subsequent
 * network calls don't recompute (canvas render costs ~2ms).
 *
 * Never throws — returns null if the browser blocks a signal.
 */

const CACHE_KEY = "rs_device_fp_v1";

async function sha256Hex(str) {
    const buf = new TextEncoder().encode(str);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

function canvasSignature() {
    try {
        const c = document.createElement("canvas");
        c.width = 220;
        c.height = 30;
        const ctx = c.getContext("2d");
        if (!ctx) return "";
        ctx.textBaseline = "top";
        ctx.font = "14px 'Arial'";
        ctx.fillStyle = "#f60";
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = "#069";
        ctx.fillText("NaukriDaddy 🎁 abcXYZ", 2, 15);
        ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText("NaukriDaddy 🎁 abcXYZ", 4, 17);
        // Data URL is deterministic per (renderer + fonts + antialiasing).
        return c.toDataURL();
    } catch {
        return "";
    }
}

export async function computeDeviceFingerprint() {
    try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) return cached;
    } catch { /* private mode */ }

    try {
        const nav = window.navigator || {};
        const scr = window.screen || {};
        const tz = (Intl.DateTimeFormat().resolvedOptions() || {}).timeZone || "";
        const parts = [
            nav.userAgent || "",
            nav.language || "",
            String(nav.hardwareConcurrency ?? ""),
            `${scr.width || 0}x${scr.height || 0}x${scr.colorDepth || 0}`,
            tz,
            canvasSignature(),
        ].join("||");
        const hash = await sha256Hex(parts);
        try { sessionStorage.setItem(CACHE_KEY, hash); } catch { /* soft-fail */ }
        return hash;
    } catch {
        return null;
    }
}
